import Header from '@/components/header/Header.vue';
import Footer from '@/components/footer/Footer.vue';
import BookCard from '@/components/book-card/BookCard.vue';
import axios from '@/config/axios';

export default {
  name: 'AuthorDetail',
  components: {
    Header,
    Footer,
    BookCard
  },
  data() {
    return {
      authorId: '',
      author: null,
      authorCategories: [],
      showAllCategoriesModal: false,
      books: [],
      currentPage: 0,
      totalPages: 0,
      totalElements: 0,
      isLoading: true,
      errorMessage: null,
      searchQuery: '',
      sortBy: 'default',
      searchTimeout: null
    };
  },
  watch: {
    '$route.params.id': {
      handler(newId) {
        if (newId) {
          this.authorId = newId;
          this.searchQuery = '';
          this.sortBy = 'default';
          this.fetchAuthorData(0);
        }
      },
      immediate: true
    }
  },
  computed: {
    birthYear() {
      return this.author?.yob || null;
    },
    deathYear() {
      return this.author?.yod || null;
    },
    authorInitials() {
      if (!this.author || !this.author.name) return 'TG';
      const words = this.author.name.trim().split(/\s+/);
      if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    },
    authorLifeSpanLabel() {
      if (this.birthYear && this.deathYear) {
        return 'Năm sinh / Năm mất';
      } else if (this.deathYear) {
        return 'Năm mất';
      }
      return 'Năm sinh';
    },
    authorLifeSpanText() {
      if (this.birthYear && this.deathYear) {
        return `${this.birthYear} – ${this.deathYear}`;
      } else if (this.birthYear) {
        return `${this.birthYear}`;
      } else if (this.deathYear) {
        return `${this.deathYear}`;
      }
      return '';
    },
    previewCategories() {
      if (this.authorCategories && this.authorCategories.length > 0) {
        return this.authorCategories.slice(0, 5);
      }
      return [];
    },
    hasMoreCategories() {
      return this.authorCategories && this.authorCategories.length > 5;
    },
    extraCategoriesCount() {
      return Math.max(0, this.authorCategories.length - 5);
    },
    visiblePages() {
      const current = this.currentPage;
      const total = this.totalPages;
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i);
      }

      const pages = [];
      pages.push(0);

      const start = Math.max(1, current - 1);
      const end = Math.min(total - 2, current + 1);

      if (start > 1) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < total - 2) {
        pages.push('...');
      }

      pages.push(total - 1);
      return pages;
    }
  },
  methods: {
    async fetchAuthorData(page = 0) {
      this.isLoading = true;
      this.errorMessage = null;

      // Ưu tiên đọc tên tác giả từ state nếu có
      if (history.state && history.state.authorName && !this.author) {
        this.author = {
          id: this.authorId,
          name: history.state.authorName,
          yob: null,
          yod: null
        };
        document.title = `${this.author.name} - Tác giả | Lối Đọc`;
      }

      try {
        const response = await axios.get(`author/v1/get-by-id/${this.authorId}`, {
          params: {
            key: this.searchQuery ? this.searchQuery.trim() : '',
            sortBy: this.sortBy || 'default',
            page
          },
          _skip403Redirect: true
        });
        const data = response.data;
        if (data.status === '200' && data.result) {
          const res = data.result;
          this.author = {
            id: res.id,
            name: res.name,
            yob: res.yob,
            yod: res.yod
          };
          if (res.name) {
            document.title = `${res.name} - Tác giả | Lối Đọc`;
          }
          if (Array.isArray(res.categories)) {
            this.authorCategories = res.categories;
          }
          if (res.books) {
            const content = res.books.content || [];
            this.books = content.map(book => ({
              ...book,
              cover: book.cover && !(book.cover.startsWith('data:') || book.cover.startsWith('http'))
                ? 'data:image/png;base64,' + book.cover
                : book.cover
            }));
            this.currentPage = page;
            this.totalPages = res.books.totalPages || 0;
            this.totalElements = res.books.totalElements || 0;
          }
        } else if (data.status === '404') {
          this.author = null;
          this.authorCategories = [];
          this.books = [];
          this.totalPages = 0;
          this.totalElements = 0;
        } else {
          this.errorMessage = data.message || 'Không thể tải thông tin tác giả.';
        }
      } catch (err) {
        console.error('Error fetching author data:', err);
        this.errorMessage = err.response?.data?.message || 'Không thể kết nối đến máy chủ. Vui lòng thử lại sau.';
      } finally {
        this.isLoading = false;
      }
    },
    onSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.fetchAuthorData(0);
      }, 350);
    },
    onSortChange() {
      this.fetchAuthorData(0);
    },
    openCategoriesModal() {
      this.showAllCategoriesModal = true;
      document.body.style.overflow = 'hidden';
    },
    closeCategoriesModal() {
      this.showAllCategoriesModal = false;
      document.body.style.overflow = '';
    },
    goToPage(page) {
      if (page >= 0 && page < this.totalPages && page !== this.currentPage) {
        this.fetchAuthorData(page);
        window.scrollTo({ top: 250, behavior: 'smooth' });
      }
    },
    changePage(page) {
      this.goToPage(page);
    },
    clearSearch() {
      this.searchQuery = '';
      this.fetchAuthorData(0);
    },
    retry() {
      this.fetchAuthorData(0);
    }
  },
  beforeUnmount() {
    document.body.style.overflow = '';
  }
};
